import React, { useCallback, useEffect, useMemo, useState } from "react";
import PortalLayout from "@/components/PortalLayout";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Search, Users, FolderKanban, ArrowRightLeft } from "lucide-react";
import { firstOfMonth, toISO } from "./planner/timeline";
import { ProjectTimeline, TimelineLegend, EmployeeTimeline } from "./planner/ProjectTimeline";
import { AvailablePanel, AvailableFuturePanel } from "./planner/AvailablePanel";
import { Simulator } from "./planner/Simulator";

export default function Planner() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("assignments.manage");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [start, setStart] = useState(() => firstOfMonth(new Date()));
  const [monthsCount, setMonthsCount] = useState(6);
  const [tab, setTab] = useState("project"); // employee | project | simulator
  const [q, setQ] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [withResOnly, setWithResOnly] = useState(false);
  const [freeHorizon, setFreeHorizon] = useState(3); // luni: cine se eliberează în orizontul ales

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/pm/planner?start=${start}&months=${monthsCount}`);
      setData(r.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }, [start, monthsCount]);
  useEffect(() => { load(); }, [load]);

  const empRows = useMemo(() => {
    if (!data) return [];
    let r = data.by_employee || [];
    if (onlyActive) r = r.filter((x) => x.active_months > 0);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      r = r.filter((x) => x.employee.name?.toLowerCase().includes(s) || x.employee.job_title?.toLowerCase().includes(s));
    }
    return r;
  }, [data, q, onlyActive]);

  const projRows = useMemo(() => {
    if (!data) return [];
    let r = data.by_project || [];
    if (withResOnly) r = r.filter((x) => x.members.length > 0);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      r = r.filter((x) => x.project.name?.toLowerCase().includes(s) || x.project.code?.toLowerCase().includes(s));
    }
    return r;
  }, [data, q, withResOnly]);

  const months = data?.months || [];
  const today = data?.today || toISO(new Date());
  const horizonEnd = useMemo(() => {
    const d = new Date(today + "T00:00:00");
    d.setMonth(d.getMonth() + freeHorizon);
    return toISO(d);
  }, [today, freeHorizon]);
  const tabBtn = (key, Icon, label, testid) => (
    <button data-testid={testid} onClick={() => setTab(key)}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium transition-colors border ${tab === key ? "bg-[#3d68b1] text-white border-[#3d68b1]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <PortalLayout variant="staff" wide>
      <div data-testid="planner-page">
        <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
          <div>
            <div className="section-eyebrow">Resurse</div>
            <h1 className="section-title">Planner resurse</h1>
            <p className="text-[12px] text-slate-400 mt-1">Alocări planificate pe proiecte — pe baza perioadelor definite în Proiecte și alocări.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input data-testid="planner-search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={tab === "employee" ? "Caută angajat..." : "Caută proiect..."} className="pio-input pl-8 w-48" />
            </div>
            {tab !== "simulator" && (
              <>
                <select data-testid="planner-range" value={monthsCount} onChange={(e) => setMonthsCount(Number(e.target.value))}
                  className="pio-input !w-auto" title="Interval de vizualizare / planificare">
                  <option value={6}>6 luni</option>
                  <option value={12}>12 luni</option>
                  <option value={18}>18 luni</option>
                </select>
                <select data-testid="planner-free-horizon" value={freeHorizon} onChange={(e) => setFreeHorizon(Number(e.target.value))}
                  className="pio-input !w-auto" title="Marchează cu portocaliu cine se eliberează în orizontul ales">
                  <option value={1}>Eliberări: 1 lună</option>
                  <option value={3}>Eliberări: 3 luni</option>
                  <option value={6}>Eliberări: 6 luni</option>
                </select>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {tabBtn("project", FolderKanban, "Per proiect", "planner-tab-project")}
          {tabBtn("employee", Users, "Per angajat", "planner-tab-employee")}
          {canManage && tabBtn("simulator", ArrowRightLeft, "Simulator resurse", "planner-tab-simulator")}
          {tab === "employee" && (
            <label className="ml-2 flex items-center gap-1.5 text-[12px] text-slate-600 cursor-pointer select-none">
              <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)}
                data-testid="planner-only-active" className="accent-[#3d68b1]" />
              Doar cu alocare
            </label>
          )}
          {tab !== "employee" && (
            <label className="ml-2 flex items-center gap-1.5 text-[12px] text-slate-600 cursor-pointer select-none">
              <input type="checkbox" checked={withResOnly} onChange={(e) => setWithResOnly(e.target.checked)}
                data-testid="planner-with-resources" className="accent-[#3d68b1]" />
              Doar proiecte cu resurse
            </label>
          )}
          {(tab === "project" || tab === "employee") && <div className="ml-auto"><TimelineLegend /></div>}
        </div>

        {loading || !data ? (
          <div className="py-24 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : tab === "simulator" ? (
          <Simulator data={data} q={q} hideEmpty={withResOnly} onApplied={load} />
        ) : tab === "project" ? (
          <div className="flex gap-4 items-start flex-col lg:flex-row">
            <div className="surface overflow-hidden flex-1 min-w-0 w-full">
              <ProjectTimeline rows={projRows} months={months} today={today} horizonEnd={horizonEnd} />
            </div>
            <div className="w-full lg:w-72 shrink-0 space-y-4">
              <AvailablePanel items={data.available || []} />
              <AvailableFuturePanel items={data.available_future || []} today={today} horizon={freeHorizon} />
            </div>
          </div>
        ) : (
          <div className="surface overflow-hidden">
            <EmployeeTimeline rows={empRows} months={months} today={today} horizonEnd={horizonEnd} />
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
